import SchoolOutlined from '@mui/icons-material/SchoolOutlined'
import { IconProps, SvgIconTypeMap } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import { Category } from './category'
import { i18nTranslationHelper } from '@/i18n'

export namespace EnrollmentCategory {
  const t = i18nTranslationHelper.getTEnums
  export class Enum {
    static readonly COURSE = new Enum(
      Category.Enum.COURSE.title,
      t('enrollmentCategories.course.description'),
      Category.Enum.COURSE.name,
      1,
      [Category.Enum.COURSE],
      Category.Enum.COURSE.icon,
      false
    )

    static readonly HIGHER_EDUCATION = new Enum(
      t('enrollmentCategories.higherEducation.label'),
      t('enrollmentCategories.higherEducation.description'),
      'higher_education',
      2,
      [
        Category.Enum.HIGH_SCHOOL,
        Category.Enum.TECHNICAL,
        Category.Enum.UNDERGRADUATE,
        Category.Enum.GRADUATE
      ],
      SchoolOutlined,
      true
    )

    static readonly LANGUAGE = new Enum(
      Category.Enum.LANGUAGE.title,
      t('enrollmentCategories.language.description'),
      Category.Enum.LANGUAGE.name,
      3,
      [Category.Enum.LANGUAGE],
      Category.Enum.LANGUAGE.icon,
      false
    )

    static readonly MENTORING = new Enum(
      Category.Enum.MENTORING.title,
      t('enrollmentCategories.mentoring.description'),
      Category.Enum.MENTORING.name,
      4,
      [Category.Enum.MENTORING],
      Category.Enum.MENTORING.icon,
      false
    )

    public constructor(
      public readonly title: string,
      public readonly description: string,
      public readonly name: string,
      public readonly sequence: number,
      public readonly categories: Category.Enum[],
      public readonly icon?: OverridableComponent<SvgIconTypeMap<object, 'svg'>>,
      public readonly hasMultipleCategories: boolean = false
    ) {}

    public get Icon() {
      return this.icon as React.ComponentType<IconProps>
    }

    public getAvailableCategories = (restricted: Category.Enum[] = []): Category.Enum[] => {
      return this.categories
        .filter((category: Category.Enum) => category.isAvailable(restricted))
        .sort((a: Category.Enum, b: Category.Enum) => a.sequence - b.sequence)
    }
  }

  export const getByCategory = (category: Category.Enum): Enum => {
    return Object.values(EnrollmentCategory.Enum).find(enrollmentCategory =>
      enrollmentCategory.categories.includes(category)
    )
  }

  export const getOnlyMultipleByCategory = (category: Category.Enum): Enum | undefined => {
    const enrollmentCategory = getByCategory(category)
    if (enrollmentCategory.categories.length > 1) {
      return enrollmentCategory
    }
  }

  export const getAvailables = (restricted: Category.Enum[] = []): Enum[] => {
    return Object.values(EnrollmentCategory.Enum)
      .map(ec => {
        const availableCategories = ec.getAvailableCategories(restricted)

        if (availableCategories.length === 0) return null

        if (availableCategories.length === 1) {
          return new Enum(
            availableCategories[0].title,
            availableCategories[0].description,
            availableCategories[0].name,
            availableCategories[0].sequence,
            availableCategories,
            availableCategories[0].icon,
            false
          )
        }

        return new Enum(
          ec.title,
          ec.description,
          ec.name,
          ec.sequence,
          availableCategories,
          ec.icon,
          availableCategories.length > 1 ? true : false
        )
      })
      .filter((ec: Enum | null): ec is Enum => ec !== null)
      .sort((a: Enum, b: Enum): number => {
        return a.sequence - b.sequence
      })
  }

  export const getGroups = (): Enum[] =>
    Object.values(EnrollmentCategory.Enum).filter(c => c.categories.length > 1)

  export const getByName = (name: string): Enum =>
    Object.values(EnrollmentCategory.Enum).find(type => type.name.toUpperCase() === name)
}
